import { useEffect, useMemo, FC } from 'react'
import { useAtomValue } from 'jotai'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { SidebarHeader } from 'src/v2/components/sidebar-header'
import { HeaderCover } from 'src/v2/components/sidebar-header-cover'
import { MapMarkerAlt } from '@emotion-icons/fa-solid/MapMarkerAlt'
import { OpenInNew } from '@emotion-icons/material/OpenInNew'
import { Colors, Shapes } from 'src/components/theme'
import { SidebarHeaderContent } from 'src/v2/components/sidebar-header-content'
import LocationHeaderBG from 'src/assets/images/map-explorer/location-header.svg'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import { CopyButton, IconActionButton } from 'src/v2/components/copy-button'
import chroma from 'chroma-js'
import { DarkTooltip } from 'src/components/tooltip/dark-tooltip'
import styled from '@emotion/styled'
import { Divider, Link, Rating, Typography } from '@mui/material'
import { prettyPlaceType } from 'src/utils/string-modification-utils'
import { Chip } from 'src/v2/components/chip'
import { PersonalInsights } from 'src/components/sidebar-panels/personal-insights'
import { AvailableInsights } from 'src/components/sidebar-panels/available-insights'
import { getCategoryFromNaicsCode } from 'src/constants/mapbox'
import { prettyNumber } from 'src/utils/number-format'
import { UserListsPopover } from 'src/components/user-lists-popover'
import { fullAddress } from 'src/utils/full-address'
import { MiniStreetViewIcon } from 'src/components/sidebar-panels/venue-details/google-details-panel/photos/street-view-icon'
import { AlphaMapMobilityBadge } from 'src/views/map-explorer/sidebar/panels/results/mobility-badge'
import { SelectedEntityTree } from 'src/views/map-explorer/sidebar/related-results/selected-entity-tree'
import { MapExplorerPanelActions } from 'src/views/map-explorer/sidebar/panels/actions'

import * as coreAtoms from 'src/v2/state/core'
import * as selectorAtoms from 'src/v2/state/map-explorer/selector'
import * as userListsPopoverAtoms from 'src/atoms/user-lists/user-lists-popover'
import * as userListsAtoms from 'src/atoms/user-lists'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { ErrorBoundary } from 'src/components/errors/error-boundary'

const getHost = (maybeWebsite: string): string => {
  if (!maybeWebsite) {
    return ''
  }
  try {
    // if the url is not well formed this will throw
    return new URL(maybeWebsite).host
  } catch (e) {}
  return ''
}

type LocationDetailsProps = {
  address?: string
  phone?: string
  website?: string
  googleMapsUrl?: string
  rating?: number
  ratingCount?: number
  hideGoogleStreetView?: boolean
}
export const LocationDetails = (props: LocationDetailsProps) => {
  const { rating, ratingCount, address, phone, website, googleMapsUrl, hideGoogleStreetView } = props

  return (
    <FlexColumn>
      {rating && ratingCount ? (
        <FlexRow gap={Shapes.spacing[1]} align="center">
          <Rating readOnly name="rating" value={rating} precision={0.5} size="small" />
          <Chip icon={false}>
            <Typography variant="text1m">
              {rating.toFixed(1)} · ({prettyNumber(ratingCount)})
            </Typography>
          </Chip>
        </FlexRow>
      ) : null}
      {address && (
        <FlexRow gap={Shapes.spacing[1]} align="center">
          <Typography
            style={{
              maxWidth: '95%',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
            variant="text1m">
            {address}
          </Typography>
          <DarkTooltip arrow={false} title="Copy address" placement="top">
            <span>
              <CopyButton ghost textToCopy={address} />
            </span>
          </DarkTooltip>
          <DarkTooltip arrow={false} title="Open in Google Maps" placement="top">
            <a href={googleMapsUrl} target="_blank">
              <IconActionButton ghost={1} size="small">
                <OpenInNew size={14} />
              </IconActionButton>
            </a>
          </DarkTooltip>
          {!hideGoogleStreetView && (
            <DarkTooltip arrow={false} title="Toggle Street View" placement="top">
              <span>
                <MiniStreetViewIcon />
              </span>
            </DarkTooltip>
          )}
        </FlexRow>
      )}
      {(website || phone) && (
        <FlexRow gap={Shapes.spacing[2]} align="center">
          {website && (
            <Link
              style={{
                maxWidth: '50%',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
              color={Colors.neutral600}
              href={website}
              target="_blank">
              {getHost(website)}
              <OpenInNew size={14} />
            </Link>
          )}
          {website && phone && <Divider orientation="vertical" flexItem />}
          {phone && (
            <FlexRow gap={Shapes.spacing[1]} align="center">
              <Typography variant="text1m">{phone}</Typography>
              <DarkTooltip arrow={false} title="Copy phone number" placement="top">
                <span>
                  <CopyButton ghost textToCopy={phone} />
                </span>
              </DarkTooltip>
            </FlexRow>
          )}
        </FlexRow>
      )}
    </FlexColumn>
  )
}

const ReportsWrapper = styled(FlexColumn)`
  padding: ${Shapes.spacing[5]}px 0;
`
const ReportsTitle = styled(Typography)`
  padding: 0 ${Shapes.spacing[4]}px;
`

type MapExplorerSelectedLocationProps = {
  showNavigationActions?: boolean
}

export const MapExplorerSelectedLocation: FC<MapExplorerSelectedLocationProps> = ({
  showNavigationActions = false,
}) => {
  const selectedEntity = useAtomValue(coreAtoms.selectedEntity)
  const setUserListsPopoverAnchorElement = useSetRecoilState(userListsPopoverAtoms.anchorElement)
  const [userListItem, setUserListItem] = useRecoilState(userListsAtoms.userListItem)
  const savedIn = useRecoilValue(userListsAtoms.savedIn(userListItem))
  const isSaved = useRecoilValue(userListsAtoms.isSaved(userListItem))
  const inEntitySelector = Boolean(useAtomValue(selectorAtoms.supportedSelections))
  const { clearSelected, getBackText, goBack } = useMapExplorerNavigation()

  const googleMapsUrl = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_LOCATION') {
      return
    }

    const encodedName = encodeURIComponent(selectedEntity.data.google.name || '')
    const [lng, lat] = selectedEntity.data.google.geometry.location
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${selectedEntity.data.google?.place_id}&ll=${lat},${lng}`
    return mapsUrl
  }, [selectedEntity])

  const onClickSave = (evt: React.MouseEvent<HTMLElement>) => setUserListsPopoverAnchorElement(evt.currentTarget)

  useEffect(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_LOCATION') {
      return
    }
    setUserListItem({
      type: 'location',
      data: {
        locationId: selectedEntity.data['am-loc'].id,
        name: selectedEntity.data['am-loc'].name,
        address: fullAddress(selectedEntity.data['am-loc']),
      },
    })
  }, [selectedEntity])

  if (!selectedEntity || selectedEntity.type !== 'SELECTED_LOCATION') {
    return null
  }

  const location = selectedEntity.data['am-loc']
  const googleData = selectedEntity.data.google

  return (
    <>
      <ErrorBoundary wrap>
        <SidebarHeader
          cover={
            <>
              <HeaderCover
                brandId={location.brandId || undefined}
                icon={<MapMarkerAlt color={Colors.bgWhite} />}
                iconBackground={chroma(getCategoryFromNaicsCode(location.naicsCode)?.color).brighten(0.2).hex()}
                iconBorderColor={Colors.neutral100}
                badge={prettyPlaceType(googleData?.types[0] || '')}
                image={LocationHeaderBG}
                onClickSave={onClickSave}
                saved={isSaved}
                savedCount={savedIn.length}
                backText={showNavigationActions ? getBackText() : undefined}
                onBackClick={showNavigationActions ? goBack : undefined}
                onClose={showNavigationActions ? clearSelected : undefined}
              />
              <UserListsPopover />
            </>
          }
          content={
            <SidebarHeaderContent
              title={location.name}
              titleAuxiliary={location.hasMobility && <AlphaMapMobilityBadge />}>
              <LocationDetails
                rating={googleData.rating}
                ratingCount={googleData.user_ratings_total}
                address={fullAddress(location)}
                phone={googleData.phone}
                website={googleData.website}
                googleMapsUrl={googleMapsUrl}
                hideGoogleStreetView={inEntitySelector}
              />
              <SelectedEntityTree />
            </SidebarHeaderContent>
          }
        />
      </ErrorBoundary>
      {!inEntitySelector && (
        <div>
          <ErrorBoundary wrap>
            <MapExplorerPanelActions
              save={{
                onClick: onClickSave,
                isSaved,
              }}
              copy={{
                label: 'Address',
                text: googleData.formatted_address,
              }}
              streetView
              googleMapsUrl={googleMapsUrl}
            />
          </ErrorBoundary>
          <ReportsWrapper gap={Shapes.spacing[4]}>
            <ReportsTitle variant="title2" color={Colors.neutral950}>
              Reports
            </ReportsTitle>
            <ErrorBoundary wrap>
              <PersonalInsights />
            </ErrorBoundary>
            <ErrorBoundary wrap>
              <AvailableInsights only="alphamap-location" />
            </ErrorBoundary>
          </ReportsWrapper>
        </div>
      )}
    </>
  )
}
